import {
  Box,
  Edit3,
  Minus,
  Plus,
  RotateCcw,
  ShoppingCart,
  Trash2,
  Weight,
} from 'lucide-react'

import ImageComponent from '@/components/misc/Images/Image'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InvoiceFullProductData } from '@/interfaces/billing'
import { SelledProductsInterface } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'

// Action interfaces (copied from ProductManager)
interface AddProductAction {
  type: 'ADD'
  tempId: string
  product: InvoiceFullProductData
  quantity: number
  priceUnitary: { amount: number; codeCurrency: string }
}

interface ModifyProductAction {
  type: 'MODIFY'
  originalProduct: SelledProductsInterface
  newQuantity: number
  newPrice: number
  currency: string
}

interface DeleteProductAction {
  type: 'DELETE'
  originalProduct: SelledProductsInterface
  quantity: number
}

interface ProductManagerActions {
  addActions: AddProductAction[]
  modifyActions: ModifyProductAction[]
  deleteActions: DeleteProductAction[]
}

interface ProductActionsViewProps {
  existingProducts: SelledProductsInterface[]
  actions: ProductManagerActions
  onRemoveAddAction: (tempId: string) => void
  onUpdateAddQuantity: (tempId: string, newQuantity: number) => void
  onUpdateAddPrice: (
    tempId: string,
    priceUnitary: { amount: number; codeCurrency: string }
  ) => void
  onModifyProduct: (
    originalProduct: SelledProductsInterface,
    newQuantity: number,
    newPrice: number
  ) => void
  onDeleteProduct: (
    originalProduct: SelledProductsInterface,
    quantityToDelete?: number
  ) => void
  products: InvoiceFullProductData[]
  onAddProduct: (product: InvoiceFullProductData, quantity?: number) => void
  currencyTotals: Record<string, number>
  totalWeight: number
  totalVolume: number
  getAvailableStock: (productId: number) => number
  onFocusProduct?: (productId: number) => void
  documentType?: 'factura' | 'prefactura' // Add document type for context
  isTemporaryChanges?: boolean // Add temporary changes flag for context
  className?: string // Add className prop for layout control
}

export const ProductActionsView = ({
  existingProducts,
  actions,
  onRemoveAddAction,
  onUpdateAddQuantity,
  onUpdateAddPrice,
  onModifyProduct,
  onDeleteProduct,
  products,
  onAddProduct,
  currencyTotals,
  totalWeight,
  totalVolume,
  getAvailableStock,
  onFocusProduct,
  documentType,
  isTemporaryChanges,
  className,
}: ProductActionsViewProps) => {
  // Simple utility to clean numeric input and prevent leading zeros
  const cleanInput = (value: string, type: 'price' | 'quantity'): string => {
    if (type === 'quantity') {
      // Allow numbers with up to two decimal places like prices
      // Remove invalid characters
      let normalizedValue = value.replace(/[^0-9.]/g, '')
      // Only allow one dot
      const parts = normalizedValue.split('.')
      if (parts.length > 2) {
        normalizedValue = parts[0] + '.' + parts.slice(1).join('')
      }
      // Limit to two decimal places
      if (normalizedValue.includes('.')) {
        const [intPart, decPart] = normalizedValue.split('.')
        normalizedValue = intPart + '.' + (decPart ? decPart.slice(0, 2) : '')
      }
      // Handle leading zeros for integer part
      if (normalizedValue.match(/^0[0-9]/)) {
        normalizedValue = normalizedValue.replace(/^0+/, '')
      }
      return normalizedValue
    }
    // Price: digits and decimal, handle leading zeros
    let clean = value.replace(/[^0-9.]/g, '')
    const parts = clean.split('.')
    if (parts.length > 2) clean = parts[0] + '.' + parts.slice(1).join('')
    if (clean.match(/^0[0-9]/)) clean = clean.replace(/^0+/, '')
    return clean
  }

  // Helper function to format quantity display with proper decimal places
  const formatQuantity = (quantity: number): string => {
    // Show up to 2 decimal places, but remove trailing zeros
    return quantity % 1 === 0
      ? quantity.toString()
      : quantity.toFixed(2).replace(/\.?0+$/, '')
  }

  // Calculate total quantity of items instead of just counting different products
  const totalQuantity = (() => {
    let total = 0

    // Calculate from existing products (considering modifications and deletions)
    existingProducts.forEach((product) => {
      const deleteAction = actions.deleteActions.find(
        (action) => action.originalProduct.id === product.id
      )
      const modifyAction = actions.modifyActions.find(
        (action) => action.originalProduct.id === product.id
      )

      // Calculate the effective quantity after modifications and deletions
      let effectiveQuantity = product.quantity

      // Apply modifications first
      if (modifyAction) {
        effectiveQuantity = modifyAction.newQuantity
      }

      // Apply partial deletions
      if (deleteAction) {
        effectiveQuantity = Math.max(
          0,
          effectiveQuantity - deleteAction.quantity
        )
      }

      total += effectiveQuantity
    })

    // Add quantities from add actions
    actions.addActions.forEach((action) => {
      total += action.quantity
    })

    return total
  })()

  const totalActionItems =
    actions.addActions.length +
    actions.modifyActions.length +
    actions.deleteActions.length +
    existingProducts.length // Always count all existing products

  // Handle clicking on existing product row to focus it in stock
  const handleExistingProductClick = (
    product: SelledProductsInterface,
    event: React.MouseEvent
  ) => {
    // Prevent action if clicking on button
    if ((event.target as HTMLElement).closest('button')) {
      return
    }

    // Focus the product in the stock view
    onFocusProduct?.(product.productId)
  }

  // Handle partial deletion (subtract 1 from product)
  const handlePartialDelete = (product: SelledProductsInterface) => {
    onDeleteProduct(product, 1)
  }

  // Handle full deletion
  const handleFullDelete = (product: SelledProductsInterface) => {
    onDeleteProduct(product)
  }

  // Handle adding one unit to existing product
  const handleAddOne = (product: SelledProductsInterface) => {
    // Find the corresponding full product data in the products array
    const fullProduct = products.find(
      (p: InvoiceFullProductData) => p.id === product.productId
    )

    if (fullProduct) {
      onAddProduct(fullProduct)
    }
  }

  // Handle adjusting delete quantity for products in "eliminando" state
  const handleAdjustDeleteQuantity = (
    deleteAction: DeleteProductAction,
    adjustment: number
  ) => {
    const newDeleteQuantity = deleteAction.quantity + adjustment

    // If reducing to 0, remove the delete action entirely
    if (newDeleteQuantity <= 0) {
      // Remove delete action by setting quantity to 0
      onDeleteProduct(deleteAction.originalProduct, 0)
      return
    }

    // If increasing beyond original quantity, cap it
    const maxDeleteQuantity = deleteAction.originalProduct.quantity
    const finalDeleteQuantity = Math.min(newDeleteQuantity, maxDeleteQuantity)

    onDeleteProduct(deleteAction.originalProduct, finalDeleteQuantity)
  }

  // Get remaining quantity for a product being partially deleted
  const getRemainingQuantity = (
    originalProduct: SelledProductsInterface
  ): number => {
    const deleteAction = actions.deleteActions.find(
      (action) => action.originalProduct.id === originalProduct.id
    )
    return deleteAction
      ? originalProduct.quantity - deleteAction.quantity
      : originalProduct.quantity
  }

  // Check if a product is being edited (has modify or delete actions)
  const isProductBeingEdited = (productId: number): boolean => {
    return (
      actions.modifyActions.some(
        (action) => action.originalProduct.id === productId
      ) ||
      actions.deleteActions.some(
        (action) => action.originalProduct.id === productId
      )
    )
  }

  // Handle restoring a product to its original state
  const handleRestoreProduct = (product: SelledProductsInterface) => {
    // Remove both modify and delete actions for this product
    onDeleteProduct(product, 0) // This removes delete actions
    // Reset to original values to remove modify actions
    onModifyProduct(product, product.quantity, product.priceUnitary.amount)
  }

  return (
    <div className={cn('flex h-full flex-col space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Productos y Acciones</h3>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <ShoppingCart className="h-4 w-4" />
          <span>
            {totalQuantity} elemento{totalQuantity !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Products and Actions List */}
      <ScrollArea className="relative h-[100px] flex-grow rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">Producto</TableHead>
              <TableHead className="w-[110px] text-right">
                Precio Unit.
              </TableHead>
              <TableHead className="w-[90px] text-right">Cantidad</TableHead>
              <TableHead className="w-[120px] text-right">Subtotal</TableHead>
              <TableHead className="w-[100px] text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totalActionItems === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-40 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <ShoppingCart className="h-8 w-8 text-muted-foreground/60" />
                    <p className="mt-2">No hay productos seleccionados</p>
                    <p className="text-sm">
                      Añade productos desde el inventario
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* All Existing Products (always visible) */}
                {existingProducts.map((product) => {
                  const modifyAction = actions.modifyActions.find(
                    (action) => action.originalProduct.id === product.id
                  )
                  const isBeingEdited = isProductBeingEdited(product.id)
                  const isBeingModified = !!modifyAction
                  // For modified products, show the modified values
                  const displayQuantity = modifyAction
                    ? modifyAction.newQuantity
                    : product.quantity
                  const displayPrice = modifyAction
                    ? modifyAction.newPrice
                    : product.priceUnitary.amount
                  const displaySubtotal = displayQuantity * displayPrice

                  const productExists = existingProducts.some(
                    (existingProduct) =>
                      existingProduct.productId ===
                      products.find((p) => p.id === product.productId)?.id
                  )

                  return (
                    <TableRow
                      key={`existing-${product.id}`}
                      className={cn(
                        'cursor-pointer bg-background transition-colors',
                        isBeingEdited
                          ? 'border-l-4 border-l-orange-400 bg-orange-50/50'
                          : 'hover:bg-muted/50'
                      )}
                      onClick={(e) => handleExistingProductClick(product, e)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <ImageComponent
                            src={product.image?.src}
                            hash={product.image?.blurHash}
                            className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg"
                          />
                          <div className="flex min-w-0 flex-col">
                            <div className="flex items-center gap-2">
                              {isBeingEdited && (
                                <Edit3 className="h-3 w-3 text-orange-600" />
                              )}
                              <span className="text-start font-medium">
                                {product.name}
                                {isBeingEdited ? (
                                  <span className="text-xs font-medium text-orange-600">
                                    {' '}
                                    (editando)
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {' '}
                                    (existente)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex flex-col justify-between text-start text-xs text-muted-foreground">
                              <span className="italic">
                                {product.variation
                                  ? product.variation.name
                                  : '-'}
                              </span>
                              <div className="flex items-start gap-2">
                                {product.totalWeight && (
                                  <div className="flex items-center gap-1">
                                    <Weight className="inline h-3 w-3" />
                                    <span className="min-w-fit">
                                      {product.totalWeight} kg
                                    </span>
                                  </div>
                                )}
                                {product.totalCubaje && (
                                  <div className="flex items-center gap-1">
                                    <Box className="inline h-3 w-3" />
                                    <span className="min-w-fit">
                                      {product.totalCubaje} m³
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {isBeingModified ? (
                          <Input
                            type="text"
                            value={modifyAction!.newPrice.toString()}
                            onChange={(e) => {
                              const cleaned = cleanInput(
                                e.target.value,
                                'price'
                              )
                              const newPrice = parseFloat(cleaned) || 0
                              onModifyProduct(
                                product,
                                modifyAction!.newQuantity,
                                newPrice
                              )
                            }}
                            className="h-7 w-20 text-right text-sm"
                            placeholder="0.00"
                            inputMode="decimal"
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.select()}
                          />
                        ) : (
                          <span className="text-sm">
                            {displayPrice.toFixed(2)}{' '}
                            {product.priceUnitary.codeCurrency}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isBeingModified ? (
                          <Input
                            type="text"
                            value={formatQuantity(modifyAction!.newQuantity)}
                            onChange={(e) => {
                              const cleaned = cleanInput(
                                e.target.value,
                                'quantity'
                              )
                              const newQuantity = parseFloat(cleaned) || 1
                              const availableStock = getAvailableStock(
                                product.productId
                              )
                              const maxQuantity =
                                availableStock + product.quantity
                              const finalQuantity = Math.min(
                                newQuantity,
                                maxQuantity
                              )
                              onModifyProduct(
                                product,
                                finalQuantity,
                                modifyAction!.newPrice
                              )
                            }}
                            className="h-7 w-16 text-right text-sm"
                            placeholder="1"
                            inputMode="decimal"
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.select()}
                          />
                        ) : (
                          <span className="text-sm">
                            {formatQuantity(displayQuantity)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {displaySubtotal.toFixed(2)}{' '}
                        {product.priceUnitary.codeCurrency}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {isBeingEdited ? (
                            // Restore button when being edited
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRestoreProduct(product)
                              }}
                              title="Restaurar a estado original"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          ) : (
                            <>
                              {/* Yellow minus button for partial deletion (only if quantity > 1) */}
                              {product.quantity > 1 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePartialDelete(product)
                                  }}
                                  title="Eliminar 1 unidad"
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                              )}
                              {/* Trash button for full deletion */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleFullDelete(product)
                                }}
                                title="Eliminar producto"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          {/* Plus button for adding one unit */}
                          {productExists && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-green-600 hover:bg-green-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleAddOne(product)
                              }}
                              title="Añadir 1 unidad"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}

                {actions.deleteActions.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <Separator className="bg-red-200" />
                      <div className="bg-red-50/30 px-3 py-1 text-xs font-medium text-red-700">
                        <Minus className="mr-1 inline h-3 w-3" />
                        Producto{actions.deleteActions.length > 1 ? 's' : ''} a
                        eliminar
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Deleted Products (separate "eliminando" rows) */}
                {actions.deleteActions
                  .slice() // Create a copy to avoid mutating the original array
                  .sort((a, b) => a.originalProduct.id - b.originalProduct.id) // Sort by product ID for consistent order
                  .map((action) => {
                    const remainingQuantity = getRemainingQuantity(
                      action.originalProduct
                    )
                    const isFullDeletion =
                      action.quantity === action.originalProduct.quantity
                    const canIncreaseDelete =
                      action.quantity < action.originalProduct.quantity

                    return (
                      <TableRow
                        key={`delete-${action.originalProduct.id}`}
                        className="border-l-4 border-l-red-500 bg-red-50/50"
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <ImageComponent
                              src={action.originalProduct.image?.src}
                              hash={action.originalProduct.image?.blurHash}
                              className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg opacity-50"
                            />
                            <div className="flex min-w-0 flex-col">
                              <div className="flex items-center gap-2">
                                <Minus className="h-3 w-3 text-red-600" />
                                <span className="font-medium text-red-700">
                                  {action.originalProduct.name}
                                </span>
                                <span className="text-xs font-medium text-red-600">
                                  (eliminando)
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {isFullDeletion ? (
                                  'Eliminación completa'
                                ) : (
                                  <>
                                    Eliminando {formatQuantity(action.quantity)}{' '}
                                    de{' '}
                                    {formatQuantity(
                                      action.originalProduct.quantity
                                    )}
                                    {remainingQuantity > 0 && (
                                      <span className="ml-2 text-green-600">
                                        (Quedan:{' '}
                                        {formatQuantity(remainingQuantity)})
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-red-600">
                            -
                            {action.originalProduct.priceUnitary.amount.toFixed(
                              2
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-semibold text-red-600">
                            -{formatQuantity(action.quantity)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <span className="text-red-600">
                            -
                            {(
                              action.quantity *
                              action.originalProduct.priceUnitary.amount
                            ).toFixed(2)}{' '}
                            {action.originalProduct.priceUnitary.codeCurrency}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {/* Minus button: increase delete quantity or restore */}
                            {canIncreaseDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                                onClick={() => {
                                  if (canIncreaseDelete) {
                                    handleAdjustDeleteQuantity(action, 1)
                                  } else {
                                    // Restore product completely (remove delete action)
                                    handleAdjustDeleteQuantity(
                                      action,
                                      -action.quantity
                                    )
                                  }
                                }}
                                title={
                                  canIncreaseDelete
                                    ? 'Eliminar 1 unidad'
                                    : 'Restaurar producto'
                                }
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                            )}
                            {/* Plus button: reduce delete quantity */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-green-600 hover:bg-green-50"
                              onClick={() =>
                                handleAdjustDeleteQuantity(action, -1)
                              }
                              title="Aumentar 1 unidad"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}

                {/* Add Actions Separator */}
                {actions.addActions.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <Separator className="bg-green-200" />
                      <div className="bg-green-50/30 px-3 py-1 text-xs font-medium text-green-700">
                        <Plus className="mr-1 inline h-3 w-3" />
                        Producto{actions.addActions.length > 1 ? 's' : ''} nuevo
                        {actions.addActions.length > 1 ? 's' : ''}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Add Actions */}
                {actions.addActions.map((action) => {
                  const availableStockForAction = getAvailableStock(
                    action.product.id
                  )

                  return (
                    <TableRow
                      key={action.tempId}
                      className="border-l-4 border-l-green-500 bg-green-50/50"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <ImageComponent
                            src={action.product.images?.[0]?.src}
                            hash={action.product.images?.[0]?.blurHash}
                            className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg"
                          />
                          <div className="flex min-w-0 flex-col">
                            <div className="flex items-center gap-2">
                              <Plus className="h-3 w-3 flex-shrink-0 text-green-600" />
                              <span className="text-start font-medium">
                                {action.product.name}
                              </span>
                              <span className="text-xs font-medium text-green-600">
                                (nuevo)
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {action.product.netWeight && (
                                <span>
                                  <Weight className="inline h-3 w-3" />{' '}
                                  {action.product.netWeight} kg
                                </span>
                              )}
                              {action.product.cubaje && (
                                <span className="ml-2">
                                  <Box className="inline h-3 w-3" />{' '}
                                  {action.product.cubaje} m³
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="text"
                            value={action.priceUnitary.amount.toString()}
                            onChange={(e) => {
                              const cleaned = cleanInput(
                                e.target.value,
                                'price'
                              )
                              const newAmount = parseFloat(cleaned) || 0
                              onUpdateAddPrice(action.tempId, {
                                amount: newAmount,
                                codeCurrency: action.priceUnitary.codeCurrency,
                              })
                            }}
                            className="h-7 w-20 text-right text-sm"
                            placeholder="0.00"
                            inputMode="decimal"
                            onFocus={(e) => e.target.select()}
                          />
                          <Select
                            value={action.priceUnitary.codeCurrency}
                            onValueChange={(currency) => {
                              onUpdateAddPrice(action.tempId, {
                                amount: action.priceUnitary.amount,
                                codeCurrency: currency,
                              })
                            }}
                          >
                            <SelectTrigger className="h-7 w-16 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="CUP">CUP</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Input
                            type="text"
                            value={formatQuantity(action.quantity)}
                            onChange={(e) => {
                              const cleaned = cleanInput(
                                e.target.value,
                                'quantity'
                              )
                              const newQuantity = parseFloat(cleaned) || 1
                              onUpdateAddQuantity(action.tempId, newQuantity)
                            }}
                            className="h-7 w-16 text-right text-sm"
                            placeholder="1"
                            inputMode="decimal"
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {(action.quantity * action.priceUnitary.amount).toFixed(
                          2
                        )}{' '}
                        {action.priceUnitary.codeCurrency}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => onRemoveAddAction(action.tempId)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </>
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      {/* Summary */}
      {totalActionItems > 0 && (
        <div className="flex-shrink-0 rounded-md bg-muted p-4">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between font-medium">
              <span>Subtotal:</span>
              <div className="flex flex-col gap-1">
                {Object.entries(currencyTotals).map(([currency, total]) => (
                  <p key={currency}>
                    {total.toFixed(2)} {currency}
                  </p>
                ))}
              </div>
            </div>
            {totalWeight > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Weight className="h-4 w-4" /> Peso Total:
                </span>
                <span>{totalWeight.toFixed(2)} kg</span>
              </div>
            )}
            {totalVolume > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Box className="h-4 w-4" /> Volumen Total:
                </span>
                <span>{totalVolume.toFixed(3)} m³</span>
              </div>
            )}

            {/* Stock Impact Summary */}
            {(actions.addActions.length > 0 ||
              actions.modifyActions.length > 0 ||
              actions.deleteActions.length > 0) && (
              <>
                <Separator className="my-2" />
                <div className="text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>
                      {isTemporaryChanges
                        ? `Impacto planificado en ${
                            documentType === 'factura'
                              ? 'stock real'
                              : 'stock reservado'
                          }:`
                        : documentType
                          ? `Impacto en inventario (${
                              documentType === 'factura'
                                ? 'stock real'
                                : 'stock reservado'
                            }):`
                          : 'Impacto en inventario:'}
                    </span>
                  </div>
                  <div className="mt-1 space-y-1">
                    {actions.addActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-green-600">
                          •{' '}
                          {actions.addActions.reduce(
                            (sum, action) => sum + action.quantity,
                            0
                          )}{' '}
                          unidad(es){' '}
                          {isTemporaryChanges ? 'planificadas' : 'agregadas'}
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'a reservar' : 'reservadas'})`
                            : ''}
                        </span>
                      </div>
                    )}
                    {actions.modifyActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-600">
                          • {actions.modifyActions.length} producto(s)
                          modificado(s)
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'reserva planificada' : 'reserva'})`
                            : ''}
                        </span>
                      </div>
                    )}
                    {actions.deleteActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-red-600">
                          •{' '}
                          {actions.deleteActions.reduce(
                            (sum, action) => sum + action.quantity,
                            0
                          )}{' '}
                          unidad(es){' '}
                          {isTemporaryChanges ? 'a liberar' : 'liberadas'}
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'des-reservar' : 'des-reservadas'})`
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
